import { ContentLink } from "@components/cms/link/ContentLink";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import {
    useAddProductToCart,
    useRemoveProductFromCart,
} from "@lib/commerce/hooks/productOperations";
import { useStore } from "@store/store";
import { useEffect, useState } from "react";
import type { Cart, Product } from "@graphql/generated/types";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { GiftWrappingPrice } from "@ecco/ui/product-row";
import { Text } from "@ecco/ui/text";
import { Checkbox } from "@ecco/ui/checkbox";
import { GIFT_WRAPPING_KEY, GIFT_WRAPPING_SKU } from "@lib/constants/product";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { SummaryRow } from "./summary-row";
import { summaryStyles } from "./SummaryStyles";
import { useLocale } from "next-intl";

export const GiftWrapping = ({
    shouldDisplayCheckbox = true,
    shouldDisplayInSummary = false,
}: {
    shouldDisplayCheckbox?: boolean;
    shouldDisplayInSummary?: boolean;
}) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.giftWrapping.link,
            CommonCMS.giftWrapping.label,
            CommonCMS.giftWrapping.free,
        ]);
    })();

    const headers = {
        giftWrappingLabel: microcopies[CommonCMS.giftWrapping.label],
        giftWrappingLink: microcopies[CommonCMS.giftWrapping.link],
        giftWrappingFree: microcopies[CommonCMS.giftWrapping.free],
    };

    const executeAddProductToCart = useAddProductToCart();
    const executeRemoveProductToCart = useRemoveProductFromCart();

    const cart = useStore((state) => state.data.me.cartInfo.cart) as Cart;
    const storeData = useStore((state) => state.data.storeData);

    const locale = useLocale();

    const addGiftWrapping = async () => {
        await executeAddProductToCart({
            cartId: cart?.id,
            cartVersion: cart?.version,
            storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
            sku: GIFT_WRAPPING_SKU,
            quantity: 1,
            distributionChannel: process.env.NEXT_PUBLIC_STORE_KEY,
            locale,
            ...(storeData?.storeId && {
                supplyChannel: {
                    typeId: "channel",
                    id: storeData.supplyChannel?.[0]?.id,
                },
            }),
        });
    };

    const removeGiftWrapping = async () => {
        const giftWrappingItem = cart.lineItems.find((it) => it.productKey === GIFT_WRAPPING_KEY);

        if (giftWrappingItem) {
            await executeRemoveProductToCart({
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                lineItemId: giftWrappingItem.id,
                quantity: 1,
                locale,
            });
        }
    };

    const giftWrappingInCart = cart?.lineItems.find((it) => it.productKey === GIFT_WRAPPING_KEY);

    const [giftWrappingPrice, setGiftWrappingPrice] = useState(null);

    useEffect(() => {
        if (cart?.lineItems.length === 1 && giftWrappingInCart) {
            removeGiftWrapping();
        }
    }, [cart?.lineItems, giftWrappingInCart]);

    useEffect(() => {
        ClientFetchService.get<{ product: Product }>(`/api/product/getByKey/${GIFT_WRAPPING_KEY}`)
            .then((result) => {
                setGiftWrappingPrice(result.product.masterData.current.allVariants[0].price.value);
            })
            .catch((error) => {
                Logger.error(ServiceType.NEXT_API, `Gift wrapping product fetching error`, {
                    tag: LogTag.PRODUCT,
                    error,
                });
            });
    }, []);

    const isDiscounted =
        !!giftWrappingInCart &&
        giftWrappingInCart?.totalPrice?.centAmount < giftWrappingPrice?.centAmount;

    const isFree =
        (!!giftWrappingInCart && giftWrappingInCart?.totalPrice?.centAmount === 0) ||
        giftWrappingPrice?.centAmount === 0;

    const giftWrappingLabel = (
        <Text sx={summaryStyles.text}>
            {headers.giftWrappingLabel}{" "}
            <ContentLink
                link={{
                    kind: "modal",
                    modalType: ModalTypes.giftWrapping,
                    label: headers.giftWrappingLink,
                }}
                variant="link"
            >
                {headers.giftWrappingLink}
            </ContentLink>
        </Text>
    );

    const getGiftWrappingPriceComponent = () => {
        if (!giftWrappingPrice) {
            return <></>;
        } else {
            return isFree ? (
                <Text sx={summaryStyles.text}>{headers.giftWrappingFree}</Text>
            ) : (
                <GiftWrappingPrice
                    priceNow={
                        isDiscounted
                            ? {
                                  data: giftWrappingInCart?.totalPrice,
                                  locale,
                                  sx: { marginRight: 0 },
                              }
                            : { data: giftWrappingPrice, locale }
                    }
                    priceBefore={isDiscounted ? { data: giftWrappingPrice, locale } : undefined}
                />
            );
        }
    };

    return (!shouldDisplayCheckbox && shouldDisplayInSummary) || shouldDisplayCheckbox ? (
        <SummaryRow
            title={
                shouldDisplayCheckbox ? (
                    <Checkbox
                        onChange={(ev) => {
                            if (ev.target.checked) {
                                addGiftWrapping();
                            } else {
                                removeGiftWrapping();
                            }
                        }}
                        isChecked={!!giftWrappingInCart}
                    >
                        {giftWrappingLabel}
                    </Checkbox>
                ) : (
                    giftWrappingLabel
                )
            }
            toReplacement={getGiftWrappingPriceComponent()}
            textStyle={summaryStyles.text}
            containerStyle={{
                ".chakra-money": {
                    mb: 0,
                },
            }}
        />
    ) : null;
};
